import { AnalysisErrorAlert as ErrorAlert } from './AnalysisErrorAlert.tsx';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button, Notice, linkStyles } from '@lumlab/platform-ui';
import { analysisCommand } from './analysis-api.ts';

const downloadableArtifacts = ['review.json', 'output.json', 'dce.zip'] as const;
const runResult = z.object({
    results: z.array(
        z.object({
            payload: z.object({
                input: z.object({
                    outcome: z.string(),
                    review: z.object({ state: z.string(), unresolvedIssues: z.array(z.json()) }),
                }),
                artifacts: z.record(z.string(), z.json()),
            }),
        }),
    ),
});
export function AnalysisRunArtifacts({ caseId, runId }: { caseId: string; runId: string }) {
    const { t } = useTranslation();
    const [url, setUrl] = useState<string | null>(null);
    const query = useQuery({
        queryKey: ['analysis', 'artifacts', runId],
        queryFn: async () => runResult.parse(await analysisCommand({ operation: 'get-run', id: runId })),
    });
    const download = useMutation({
        mutationFn: async (artifact: 'review.json' | 'output.json' | 'dce.zip') =>
            z.object({ url: z.url() }).parse(
                await analysisCommand({
                    operation: 'download',
                    caseId,
                    runId: artifact === 'dce.zip' ? null : runId,
                    referenceNumber: null,
                    artifact,
                }),
            ),
        onSuccess: (data) => setUrl(data.url),
    });
    const result = query.data?.results[0]?.payload;
    return (
        <div className="space-y-3">
            {query.error && <ErrorAlert error={query.error} />}
            {result && (
                <Notice>
                    {t('tenders.arbitration.result', {
                        outcome: t(`tenders.arbitration.outcome.${result.input.outcome}`),
                        review: t(`tenders.arbitration.reviewState.${result.input.review.state}`),
                        count: result.input.review.unresolvedIssues.length,
                    })}
                </Notice>
            )}
            <div className="flex flex-wrap gap-3">
                {downloadableArtifacts
                    .filter((artifact) => artifact === 'dce.zip' || result?.artifacts[artifact])
                    .map((artifact) => (
                        <Button
                            key={artifact}
                            variant="default"
                            loading={download.isPending}
                            onClick={() => {
                                setUrl(null);
                                download.mutate(artifact);
                            }}
                        >
                            {t(`tenders.arbitration.artifact.${artifact}`)}
                        </Button>
                    ))}
            </div>
            {download.error && <ErrorAlert error={download.error} />}
            {url && (
                <a href={url} className={linkStyles} target="_blank" rel="noreferrer">
                    {t('tenders.arbitration.openArtifact')}
                </a>
            )}
        </div>
    );
}
